/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useState } from 'react';
import appTheme from '../../common/theme/appTheme.json';
import styled from 'styled-components';
import { setFlightLegToDelete, showDeleteLegConfirmation } from '../../../store/flightLegs/actions';
import { useDispatch } from 'react-redux';
import { TableCell, TableRow, IconButton, Avatar, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import WarningIcon from '@mui/icons-material/Warning';
import { Draggable } from 'react-beautiful-dnd';
import { FlightLegInfo } from '../../../shared/entities/flightLeg';
import FlightLegTakeoffTimeModal from './FlightLegTakeoffTimeModal';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';

export interface DraggableLegContainerProps {
  flightLeg: FlightLegInfo;
  index: number;
  warning: boolean;
  missionPlanId: number | null;
}

const StyledTableRow = styled(TableRow)`
  ${(props: any) => (props.isdragging ? `display: table;` : ``)};
`;

const StyledTableBodyCell = styled(TableCell)`
  text-align: center;
  min-width: 0px;
  font-size: 14px;
  padding: 8px;
  font-family: ${appTheme.viasatFontFamilyRegular};
  background-color: ${appTheme.mptPrimaryBackgroundColor};
  border-bottom-color: ${appTheme.mptRowBorderBottom};
  color: ${appTheme.primaryTextColor};
`;

const StyledIconCell = styled(TableCell)`
  text-align: center;
  padding: 8px;
  min-width: 0px;
  background-color: ${appTheme.mptPrimaryBackgroundColor};
  border-bottom-color: ${appTheme.mptRowBorderBottom};
  color: ${appTheme.primaryTextColor};
`;

const StyledIconButton = styled(IconButton)`
  margin: 0px;
  padding: 0px;
  border-bottom-color: ${appTheme.mptRowBorderBottom};
  color: ${appTheme.primaryButtonTextColor};
  overflow: hidden;
`;

const StyledAvatar = styled(Avatar)`
  width: 25px;
  height: 25px;
  background-color: ${appTheme.primaryTextColor};
  color: ${appTheme.mptPrimaryBackgroundColor};
  font-size: 12px;
  margin: auto;
`;

const DraggableLegContainer = (props: DraggableLegContainerProps) => {
  const { flightLeg, index, warning, missionPlanId } = props;
  const [addTimeDialogOpen, setAddTimeDialogOpen] = useState<boolean>(false);

  const dispatch = useDispatch();

  const handleDelete = useCallback(
    (flightLeg: FlightLegInfo) => {
      dispatch(setFlightLegToDelete(flightLeg));
      dispatch(showDeleteLegConfirmation(true));
    },
    [dispatch],
  );

  const convertDuration = (durationMin: number) => {
    const totalHours = Math.floor(durationMin / 60);
    const mins = Math.round(durationMin - totalHours * 60);

    return totalHours + 'hr ' + mins + 'm';
  };

  return (
    <>
      <Draggable draggableId={flightLeg.id.toString()} index={index} key={flightLeg.id}>
        {(dragProvided: any, dragSnapshot: any) => (
          <StyledTableRow
            ref={dragProvided.innerRef}
            isdragging={dragSnapshot.isDragging ? 1 : 0}
            {...dragProvided.draggableProps}
            key={flightLeg.id}
          >
            <StyledIconCell sx={{ width: '30px' }}>
              <div {...dragProvided.dragHandleProps}>
                {warning ? <WarningIcon fontSize="small" /> : <DragHandleIcon fontSize="small" />}
              </div>
            </StyledIconCell>
            <StyledTableBodyCell sx={{ width: '25px' }} isdragging={dragSnapshot.isDragging ? 1 : 0}>
              <StyledAvatar>{flightLeg.legOrder}</StyledAvatar>
            </StyledTableBodyCell>
            <StyledTableBodyCell
              isdragging={dragSnapshot.isDragging ? 1 : 0}
              sx={{ color: appTheme.primaryTextColor, width: '80px' }}
            >
              {flightLeg.origin}
            </StyledTableBodyCell>
            <StyledTableBodyCell isdragging={dragSnapshot.isDragging ? 1 : 0}>
              <Button
                sx={{ fontSize: 16, fontWeight: 400, color: appTheme.mptFlightLegBlue, textDecoration: 'underline' }}
                variant="text"
                onClick={() => {
                  setAddTimeDialogOpen(true);
                }}
              >
                {flightLeg?.userSetTakeoffTimeMin === -1
                  ? 'Add Time'
                  : `${Math.floor(flightLeg.userSetTakeoffTimeMin / 60)
                      .toString()
                      .padStart(2, '0')}:${(flightLeg.userSetTakeoffTimeMin % 60).toString().padStart(2, '0')}z`}
              </Button>
            </StyledTableBodyCell>
            <StyledTableBodyCell sx={{ width: '70px' }} isdragging={dragSnapshot.isDragging ? 1 : 0}>
              {convertDuration(flightLeg.durationMin)}
            </StyledTableBodyCell>
            <StyledTableBodyCell
              isdragging={dragSnapshot.isDragging ? 1 : 0}
              sx={{ color: appTheme.primaryTextColor, width: '55px' }}
            >
              {flightLeg.destination}
            </StyledTableBodyCell>
            <StyledTableBodyCell sx={{ width: '80px' }} isdragging={dragSnapshot.isDragging ? 1 : 0}>
              {flightLeg.numberOfWaypoints}
            </StyledTableBodyCell>
            <StyledTableBodyCell sx={{ width: '100px' }} isdragging={dragSnapshot.isDragging ? 1 : 0}>
              {flightLeg.aircraftType ?? EMPTY_FIELD_STRING_PLACEHOLDER}
            </StyledTableBodyCell>
            <StyledIconCell sx={{ width: '30px' }} isdragging={dragSnapshot.isDragging ? 1 : 0}>
              <StyledIconButton
                onClick={() => {
                  handleDelete(flightLeg);
                }}
              >
                <DeleteIcon fontSize="small" />
              </StyledIconButton>
            </StyledIconCell>
          </StyledTableRow>
        )}
      </Draggable>
      <FlightLegTakeoffTimeModal
        flightLeg={flightLeg}
        missionPlanId={missionPlanId}
        open={addTimeDialogOpen}
        onClose={() => {
          setAddTimeDialogOpen(false);
        }}
      />
    </>
  );
};

export default DraggableLegContainer;
